import type {
	PaginationDto,
	PaginationResponseDto
} from "core/dto";
import { ITEM_STATUS, LOCALE, ORDER_BY } from "core/interfaces";
import type { PageMetaDto } from "shared/dto";

import { ITEMS_PER_PAGE } from "~/common/constants/items-per-page";
import { getItemAdaptedDefault } from "~/common/defaults/item.default";
import { getPaginationDefault } from "~/common/defaults/pagination.default";
import { getPaginationResponseDefault } from "~/common/defaults/pagination-response.default";
import { itemNormalize } from "~/common/helpers/item-adapter";
import { serializeItemFilters } from "~/common/helpers/serialize-item-filters";
import type { IdDto } from "~/common/interfaces/dto/id.dto";
import type { CreateItemDto } from "~/common/interfaces/dto/items/create-item.dto";
import type { GetAllItemsDto } from "~/common/interfaces/dto/items/get-all-items.dto";
import type { UpdateItemByIdDto } from "~/common/interfaces/dto/items/update-item-by-id.dto";
import type { Item, ItemAdapted } from "~/common/interfaces/entities/item.interface";
import type { Filter } from "~/common/interfaces/filter.interface";
import type { Sort } from "~/common/interfaces/sort.interface";

export const useItemsStore = defineStore(
	"items",
	() => {
		const { locale } = useI18n();
		const itemsNormalizeWithLocale = itemNormalize.bind(
			this,
			locale as Ref<LOCALE>
		);

		const currentItem = ref<ItemAdapted>(getItemAdaptedDefault());
		const items = ref<ItemAdapted[]>([]);
		const meta = ref<PageMetaDto>(getPaginationResponseDefault());
		const recentGetAllQuery = ref<GetAllItemsDto & PaginationDto>();
		const isItemsGrouped = reactive({ value: false });
		const tableFilter = ref<Filter[]>([
			{
				name: "Status",
				value: {
					[ITEM_STATUS.BOOKED]: false,
					[ITEM_STATUS.CANCELED]: false,
					[ITEM_STATUS.IN_STOCK]: false,
					[ITEM_STATUS.REQUESTED]: false,
					[ITEM_STATUS.SOLD]: false
				}
			}
		]);
		const tableSort = ref<Sort>({
			columns: [
				"material",
				"size",
				"text",
				"note",
				"price",
				"discountedPrice",
				"status",
				"updatedAt"
			],
			orderBy: ["updatedAt", ORDER_BY.DESC]
		});

		const createOne = async (dto: CreateItemDto): Promise<void> => {
			await useNuxtApp().$apiFetch<Item>("/items", {
				method: "POST",
				body: dto
			});
		};
		const getAll = async (
			dto: GetAllItemsDto = {},
			paginationDto?: Partial<PaginationDto>
		): Promise<PaginationResponseDto<ItemAdapted>> => {
			const filters = serializeItemFilters(tableFilter.value);
			const [column, orderBy] = tableSort.value.orderBy;

			recentGetAllQuery.value = {
				...getPaginationDefault(),
				...paginationDto,
				...dto,
				limit: isItemsGrouped.value ? getPaginationDefault().limit : ITEMS_PER_PAGE,
				...filters,
				column,
				orderBy
			};

			const res = await useNuxtApp().$apiFetch<PaginationResponseDto<Item>>("/items", {
				query: recentGetAllQuery.value
			});
			items.value = res.data.map(item => itemsNormalizeWithLocale(item));
			meta.value = res.meta;

			return res as unknown as PaginationResponseDto<ItemAdapted>;
		};
		const getById = async (id: number): Promise<ItemAdapted> => {
			const item = await useNuxtApp().$apiFetch<Item>(`/items/${id}`);

			return itemsNormalizeWithLocale(item);
		};
		const patchById = async (id: number, dto: UpdateItemByIdDto): Promise<ItemAdapted> => {
			const item = await useNuxtApp().$apiFetch<Item>(`/items/${id}`, {
				method: "PATCH",
				body: dto
			});

			return itemsNormalizeWithLocale(item);
		};
		const deleteById = async (id: number): Promise<IdDto> => {
			const res = await useNuxtApp().$apiFetch<IdDto>(`/items/${id}`, {
				method: "DELETE"
			});

			return res;
		};

		const toggleGroupItems = () => {
			isItemsGrouped.value = !isItemsGrouped.value;
		};

		return {
			currentItem,
			items,
			meta,
			recentGetAllQuery,
			isItemsGrouped,
			tableFilter,
			tableSort,

			createOne,
			getAll,
			getById,
			patchById,
			deleteById,

			toggleGroupItems
		};
	},
	{
		persist: {
			paths: ["isItemsGrouped.value", "tableFilter", "tableSort.orderBy"]
		}
	}
);
